/**
 * Authentication action creators
 * This module contains functions for handling user authentication
 * @module Auth/Actions
 */
import { store } from '../../../redux/store';
import { setCurrentUser } from './reducer';
import { storage } from '../../../shared/utils/storage';
import { services } from '../../../api';
import { LoginResponse, LoginForm, LoginContent } from '../../../interfaces/api/auth/login';
import { showLoading, hideLoading } from '../../../connected-components/loader/reducer';
import { STORAGE_KEYS } from '../../../config/keys';
import { toastService } from '../../../shared/utils/toast';
import { User } from '../../../interfaces/screens/auth/login';
import i18n from '../../../translation';

/**
 * Authenticates a user with the provided credentials
 * Handles login process, error handling, and success notifications
 *
 * @param {LoginForm} body - The user's login credentials
 * @param {boolean} [hideMessage] - Optional flag to suppress success notification
 * @returns {Promise<{ success: boolean; message?: string }>} Object indicating login success or failure
 */
export const login = async (
  body: LoginForm,
): Promise<boolean> => {
  try {
    store.dispatch(showLoading());
    // Prepare FormData
    const formData = new FormData();
    formData.append('login', body.email);
    formData.append('password', body.password);
    // You may want to pass deviceUDID from somewhere else, for now use a static value or get from device
    const deviceUDID = 'wefewfewfwef';
    const res = (await services.auth.login(formData, deviceUDID)) as LoginResponse;
    if (!res || res.result !== 'success' || !res.content) {
      throw new Error('');
    }
    await startSession(res.content);
    return true;
  } catch (error: any) {
    if (process.env.REACT_APP_DEBUG === 'true') {
      console.info('LOGIN FAIL: ', error);
    }
    return false;
  } finally {
    store.dispatch(hideLoading());
  }
};

/**
 * Starts a user session after successful authentication
 * Stores user data and config in local storage and updates Redux state
 *
 * @param {LoginContent} content - The authenticated user content
 * @returns {Promise<boolean>} True if session started successfully, false otherwise
 */
export const startSession = async (content: LoginContent) => {
  try {
    const user: User = {
      id: String(content.user.id),
      email: content.user.email,
      name: content.user.first_name + ' ' + content.user.last_name,
      first_name: content.user.first_name,
      last_name: content.user.last_name,
      token: content.token,
      profession_name: content.user.profession_name,
      image_url: content.user.image_url,
    };
    storage.set(STORAGE_KEYS.USER, user);
    store.dispatch(setCurrentUser(user as User));
    return true;
  } catch (error) {
    if (process.env.REACT_APP_DEBUG === 'true') {
      console.info('LOGIN FAIL: ', error);
    }
    return false;
  }
};

/**
 * Ends the current user session (logout)
 * Clears user data, resets application state, and shows success notification
 *
 * @returns {Promise<boolean>} - A promise that resolves to `true` if logout is successful, otherwise `false`.
 */
export const endSession = async () => {
  try {
    store.dispatch(showLoading());
    // Optionally clear other session data
    storage.clear();
    store.dispatch({ type: 'logout', payload: null });
    toastService.showSuccessToast(i18n.t('login.logoutSuccessfully'));
    return true;
  } catch (error) {
    if (process.env.REACT_APP_DEBUG === 'true') {
      console.info('LOGOUT FAIL: ', error);
    }
    return false;
  } finally {
    store.dispatch(hideLoading());
  }
};
